import { Component, effect, inject, viewChildren, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    VoucherGridItemObject,
    VoucherNursingCareDiagnosisGridItemObject,
    VoucherNursingCareDiagnosisGridObject,
} from '@it2go/types';
import { VoucherDetailSituationOrpStore } from './voucher-detail-situation-orp.store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { filter } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { VoucherDetailService } from '../../../../pages/person/voucher-detail-page/voucher-detail-page.service';
import { enableFormControls, formAddText } from '../../../../../../utils/form.utils';
import { VoucherDetailSituationOrpStore2 } from './voucher-detail-situation-orp.store2';
import { InputWysiwygComponent } from '../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { RadioInputComponent } from '../../../../../../components/kit/input/radio-input/radio-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import {
    RadioYesNoInputComponent,
} from '../../../../../../components/kit/input/radio-input/radio-yes-no-input.component';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { FolderGlobalStore } from '../../../../../../store/medicalService/folder.global-store';

@Component({
    selector: 'app-voucher-detail-situation-orp',
    templateUrl: 'voucher-detail-situation-orp.component.html',
    styleUrls: ['voucher-detail-situation-orp.component.scss'],
    providers: [VoucherDetailSituationOrpStore, VoucherDetailSituationOrpStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslatePipe,
        RadioInputComponent,
        TextInputComponent,
        RadioYesNoInputComponent,
        InputWysiwygComponent,
        ButtonComponent,
    ],
})
export class VoucherDetailSituationOrpComponent {
    protected readonly store = inject(VoucherDetailSituationOrpStore);
    private readonly translate = inject(TranslateService);
    private readonly detailService = inject(VoucherDetailService);


    public readonly store2 = inject(VoucherDetailSituationOrpStore2);
    protected readonly folderStore = inject(FolderGlobalStore);

    public readonly wysiwygs = viewChildren<InputWysiwygComponent>(InputWysiwygComponent);

    form!: FormGroup;

    dependencyLevelOptions = [
        { name: 'voucher.situationORP.dependencyLevel.optionNone', value: 'none' },
        { name: 'voucher.situationORP.dependencyLevel.optionInProcess', value: 'inProcess' },
        { name: 'voucher.situationORP.dependencyLevel.option1stLevel', value: '1stLevel' },
        { name: 'voucher.situationORP.dependencyLevel.option2ndLevel', value: '2ndLevel' },
        { name: 'voucher.situationORP.dependencyLevel.option3rdLevel', value: '3rdLevel' },
        { name: 'voucher.situationORP.dependencyLevel.option4thLevel', value: '4thLevel' },
    ];

    mobilityOptions = [
        { name: 'voucher.situationORP.mobilityLevel.optionFull', value: 'full' },
        { name: 'voucher.situationORP.mobilityLevel.optionPartial', value: 'partial' },
        { name: 'voucher.situationORP.mobilityLevel.optionCompletelyImmobile', value: 'completelyImmobile' },
    ];

    selfServiceAbilityOptions = [
        { name: 'voucher.situationORP.selfCareLevel.optionFull', value: 'full' },
        { name: 'voucher.situationORP.selfCareLevel.optionPartial', value: 'partial' },
        { name: 'voucher.situationORP.selfCareLevel.optionNone', value: 'none' },
    ];

    constructor() {
        this.form = this.detailService.situationORPForm;

        effect(() => {
            enableFormControls(this.form, this.folderStore.canEdit());
        });
    }

    async addMedication() {
        const data = await this.detailService.formatActualMedicationsText();

        formAddText(this.form, 'medicationInformation', data);
    }

    addDiagnosis() {
        this.detailService
            .voucherOnce()
            .pipe(
                filter((v) => !!v),
                switchMap((voucher: VoucherGridItemObject | null) => {
                    return this.store.nursingCareSvc.getNursingCareDiagnoses(
                        FilterBuilder.filter({
                            voucherNursingCareId: voucher!.id,
                        }),
                    );
                }),
            )
            .subscribe((d: VoucherNursingCareDiagnosisGridObject) => {
                const diagnoses: string [] = [];
                d?.['items'].forEach((it: VoucherNursingCareDiagnosisGridItemObject) => {
                    diagnoses.push(it.title || '');
                });

                let data = '';
                if (diagnoses.length) {
                    data = `${this.translate.instant('voucher.messages.actualDiagnoses')}:<br />${diagnoses.join('<br />')}`;
                } else {
                    data = this.translate.instant('voucher.messages.noDiagnoses');
                }

                this.form.patchValue({
                    goal: `${this.form.value.goal ? `${this.form.value.goal}<br />` : ''}<p>${data}</p>`,
                });
            });
    }
}
